import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FlowConfigContext } from '../../../providers/FlowConfigContext';
import { Resolver } from '../../../config';
import SVGCivicMe from '../../../common/assets/svg/icons/SVGCivicMe';
import Button, { ButtonStyle } from '../../../common/components/Button/Button';
import ButtonContainer from '../../../common/components/Button/ButtonContainer';
import TitleContainer from '../../../common/components/TitleContainer/TitleContainer';
import UserFeedback from '../../../common/components/UserFeedback/UserFeedback';
import { UserFlow } from '../../../config/networkConfig';
import { MetadataContext } from '../../../providers/MetadataContext';
import { useGKNService } from '../../../providers/GatekeeperNetworkService/GatekeeperNetworkServiceContext';
import { useCredentialStore } from '../../../providers/CredentialStoreContext';
import { Routes } from '../../../config/routes';
import { useAuthentication } from '../../../providers/AuthenticationContext';
import { useValidation } from '../../../providers/ValidationContext';
import { getGknValue } from '../../../providers/GatekeeperNetworkService/utils';

export default function TokenActiveDefault(): React.ReactElement {
  const { flowSuccessful, networkName } = useContext(FlowConfigContext);
  const { validationId, fetchStatus } = useValidation();
  const { gknServiceData } = useGKNService();
  const { isLoggedIn } = useAuthentication();
  const { retrieveExistingInProgress, allCredentialsStored } = useCredentialStore();
  const { t } = useTranslation();
  const [showOkButton, setShowOkButton] = useState<boolean>(true);
  const [civicMeLink, setCivicMeLink] = useState<string>('');
  const { metadata } = useContext(MetadataContext);
  const navigate = useNavigate();

  useEffect(() => {
    const serviceConfig = Resolver.getServiceConfig();
    const civicMeUrl =
      networkName === UserFlow.CIVIC_DEMO ? serviceConfig.civicMeDemoBaseUrl : serviceConfig.civicMeBaseUrl;
    setCivicMeLink(`${civicMeUrl}/${metadata.wallet}/${serviceConfig.civicMeUrlQueryParams}&navigate=passes`);
  }, [metadata, networkName]);

  const onConfirm = useCallback(() => {
    if (getGknValue<boolean>(gknServiceData!, 'credentialPostPassCreation')) {
      if (!isLoggedIn()) {
        navigate(Routes.LOGIN);
        return;
      }
      if (!allCredentialsStored && validationId) {
        navigate(Routes.STORE_CREDENTIALS, { state: { prevPath: Routes.TOKEN_ACTIVE } });
        return;
      }
    }
    flowSuccessful();
  }, [flowSuccessful, isLoggedIn, allCredentialsStored, validationId, navigate]);

  const onUserFeedbackInProgress = (analyticsInProgress: boolean) => {
    if (analyticsInProgress) {
      setShowOkButton(false);
    } else {
      flowSuccessful();
    }
  };

  const okButtonDisabled = useMemo(() => {
    return gknServiceData?.client?.credentialStorageEnabled
      ? !!(retrieveExistingInProgress || ['in_progress', undefined].includes(fetchStatus))
      : false;
  }, [gknServiceData?.client?.credentialStorageEnabled, retrieveExistingInProgress, fetchStatus]);

  return (
    <>
      <TitleContainer center>
        <h1 data-testid="screens.tokenActive.title">{t('screens.tokenActive.title', { context: networkName })}</h1>
      </TitleContainer>
      <div
        data-testid="screens.tokenActive.civicMeMessage"
        className="mt-10 text-lg leading-6 text-textParagraph pass-status-text"
      >
        <span>{t('screens.tokenActive.civicMeMessage')}</span>
      </div>
      <div className="mt-4 mb-6">
        <a
          data-testid="CIVIC_ME_HYPERLINK"
          className="text-lg textLink"
          href={civicMeLink}
          target="_blank"
          rel="noreferrer"
        >
          <div className="inline-block align-middle">
            <span className="inline-block align-middle mr-2">{t('screens.tokenActive.visitCivicMe')}</span>
            <span className="mr-2 inline-block align-middle">
              <SVGCivicMe />
            </span>
            <span className="inline-block align-middle">{t('screens.tokenActive.civicMeLink')}</span>
          </div>
        </a>
      </div>
      <UserFeedback onUserFeedbackInProgress={onUserFeedbackInProgress} />
      {showOkButton && (
        <ButtonContainer>
          <Button
            style={ButtonStyle.DEFAULT}
            disabled={okButtonDisabled}
            loading={okButtonDisabled}
            dataTestid="OK_BUTTON"
            onClick={onConfirm}
          >
            <span>{t('screens.tokenActive.buttonLabel', { context: networkName })}</span>
          </Button>
        </ButtonContainer>
      )}
    </>
  );
}
